import * as React from "react"
import * as PropTypes from "prop-types"
import {Pagination} from '../../../components/index';
import {cloneDeep, isEqual, orderBy, debounce} from "lodash"
import {groupComparisonReport, storeComparisonReport, exportStoreComparisonReport} from "../api"
import SelectedLocationsStore from "../../../services/locations/SelectedLocationsStore"
import accessStore from "../../../services/locations/AccessStore"
import {ReportRow} from "../components/ReportRow"
import SentimentIcon from "../components/SentimentIcon"
import SessionStore from "../../../services/session/SessionStore"
import moment from 'moment';
import {FieldDescriptors, FieldType} from "../../../util/urlSync";
import {BindWithUrl, fieldDescriptors} from "../../../util/BindWithUrl";
import {SortButton} from "../components/SortButton"
import getClassName from "../../../util/styles/getClassName"
import "./sentimentStoreComparison.scss"

const columns = [
  { id: 'locations', title: "LocationsGroups", subtext: '', sortable: true },
  { id: 'average', title: 'Average', subtext: <span className="d-flex text-nowrap justify-content-center"><span className="topic-word pr-1">Average</span><span className="topic-count">(1-3)</span></span>, sortable: true, showNAForZero: true, numberOfDecimalPlaces: 1},
  { id: 'positive', classification: 'Positive', title: 'Positive', subtext: 'Mentions', sortable: true },
  { id: 'neutral', classification: 'Neutral', title: 'Neutral', subtext: 'Mentions', sortable: true },
  { id: 'negative', classification: 'Negative', title: 'Negative', subtext: 'Mentions', sortable: true },
  {
    id: 'topPositiveTopic', title: 'Top Mentioned Positive',
    subtext: <span className="d-flex flex-column justify-content-center">
      <span className="d-flex justify-content-center">
        <span className="pr-1 text-right text-capitalize topic-word">
          Topic
        </span>
        <span className="text-nowrap topic-count text-left">
          (Mentions)
        </span>
      </span>
      <span className="d-flex justify-content-center">
        <span className="pr-1 text-right text-capitalize topic-word">
          Attribute
        </span>
        <span className="text-nowrap topic-count text-left">
          (Mentions)
        </span>
      </span>
    </span>,
    sortable: true
  },
  {
    id: 'topNegativeTopic', title: 'Top Mentioned Negative',
    subtext: <span className="d-flex flex-column justify-content-center">
      <span className="d-flex justify-content-center">
        <span className="pr-1 text-right text-capitalize topic-word">
          Topic
      </span>
        <span className="text-nowrap topic-count text-left">
          (Mentions)
      </span>
      </span>
      <span className="d-flex justify-content-center">
        <span className="pr-1 text-right text-capitalize topic-word">
          Attribute
        </span>
        <span className="text-nowrap topic-count text-left">
          (Mentions)
        </span>
      </span>
    </span>,
    sortable: true
  },
];

const mapPayloadToView = (results, showGroups) => {
  const generateRow = (row) => ({
    isGroup: showGroups,
    displayName: row.displayName,
    entityId: row.entityId,
    topPositiveTopic: row.topPositiveTopic,
    topNegativeTopic: row.topNegativeTopic,
    values: {
      average: {
        current: row.stats.averagePolarity,
        decimalDelta: row.previousChange.averagePolarity
      },
      positive: {
        current: row.stats.total3Polarity,
        decimalDelta: row.previousChange.total3Polarity
      },
      neutral: {
        current: row.stats.total2Polarity,
        decimalDelta: row.previousChange.total2Polarity
      },
      negative: {
        current: row.stats.total1Polarity,
        decimalDelta: row.previousChange.total1Polarity
      },
    },
  });

  const maybeNested = (resultRows) => {
    if (!showGroups || resultRows.length === 0) return resultRows;
    const clonedRows = cloneDeep(resultRows)
    const parent = {
      ...clonedRows.find(row => row.entityId === 'Group-All'),
      children: clonedRows.filter(row => row.entityId !== 'Group-All'),
      expanded: true
    }
    return [parent]
  }

  return maybeNested(results.map(row => generateRow(row)))
}

export class SentimentStoreComparison extends React.Component {

  static propTypes = {
    storeComparisonFilters: PropTypes.shape({

      startDate: PropTypes.any,
      endDate: PropTypes.any,
      providers: PropTypes.arrayOf(PropTypes.string),
      sentiments: PropTypes.arrayOf(PropTypes.string),
      searchQuery: PropTypes.string,
      trackedTopicSetId: PropTypes.string,

      // TODO - remove from shared filters
      offset: PropTypes.number,
      limit: PropTypes.number,
      // TODO - figure this out
      showGroups: PropTypes.bool,
      entityId: PropTypes.string
    }),
    onShowGroupsChanged: PropTypes.func
  }

  constructor(props) {
    super(props);

    this.state = {
      accessStore: null,
      results: [],
      isLoading: true,
      isDownloading: false,
      sortField: 'average',
      sortOrder: 'DESC',
      hideResultsWithNoMentions: false,
      filters: {
        offset: 0,
        limit: 15,
        entities: [],
        providers: [],
        topics: [],
        showGroups: false,
        trackedTopicSetId: undefined
      }
    };
  };

  componentWillUnmount() {
    /*
     * When making async calls that call setState, the component could be unmounted,
     * this is a hacky toggle to ensure that it is mounted.
     */
    this.isCancelled = true;
  }

  currentPageResults = () => {
    const { hideResultsWithNoMentions, results, filters } = this.state;
    const { offset, limit } = filters;

    const data = hideResultsWithNoMentions ? this.hideZeroResults(results) : results;
    const sliceTo = data.length > offset + limit ? offset + limit : data.length;

    return data.slice(offset, sliceTo)
  }

  nestedUpdate = (obj, update, index, remainingLevels) => {

    if (remainingLevels === 0) {
      obj[index] = update
    } else {
      this.nestedUpdate(obj[0].children, update, index, remainingLevels - 1)
    }
  }

  expandRow = (index, item, levelsDeep) => {

    this.setState(state => {
      const resultsCopy = cloneDeep(state.results)
      const itemCopy = cloneDeep(item)

      if (!item.children) {
        this.nestedUpdate(resultsCopy, { ...itemCopy, isLoading: true }, index, levelsDeep)
      } else {
        this.nestedUpdate(resultsCopy, { ...itemCopy, expanded: !item.expanded }, index, levelsDeep)
      }
      return { results: resultsCopy }
    }, () => !item.children && this.lazyLoadChildData(index, item, levelsDeep))
  }

  lazyLoadChildData = (index, item, levelsDeep) => {
    const { startDate, endDate } = this.state.filters;
    storeComparisonReport({
      // TODO - make these filters more traceable
      ...this.state.filters,
      startDate: !!startDate ? moment(startDate).format('YYYY-MM-DD') : undefined,
      endDate: !!endDate ? moment(endDate).format('YYYY-MM-DD') : undefined,
      entities: [item.entityId]
    }).then(resp => {
      const storeComparisonResults = resp.data.data;
      const { hideResultsWithNoMentions } = this.state;

      const data = this.state.results;
      const overallResultsCopy = cloneDeep(data);
      const rowCopy = cloneDeep(item);
      const update = {
        ...rowCopy,
        expanded: !rowCopy.expanded,
        isLoading: false,
        children: mapPayloadToView(storeComparisonResults)
      }
      this.nestedUpdate(overallResultsCopy, update, index, levelsDeep)
      !this.isCancelled && this.setState(state => ({ ...state, isLoading: false, results: overallResultsCopy }))
    })
  }

  setFilter = (update) => {
    !this.isCancelled && this.setState(state => {
      const filtersCopy = cloneDeep(state.filters)
      return { filters: { ...filtersCopy, ...update } }
    })
  }

  search = () => {
    const { entityId, entities, providers, categories, topics, sentiments, startDate, endDate, showGroups, searchQuery, trackedTopicSetId } = this.state.filters;
    const request = showGroups ? groupComparisonReport : storeComparisonReport

    if (!this.isCancelled) {
      this.setState({ isLoading: true });
      request({
        entities: entityId ? [entityId] : (!!entities ? entities : undefined),
        providers,
        categories,
        topics,
        sentiments,
        searchQuery,
        trackedTopicSetId: trackedTopicSetId !== "true" ? trackedTopicSetId : undefined,
        startDate: !!startDate ? moment(startDate).format('YYYY-MM-DD') : undefined,
        endDate: !!endDate ? moment(endDate).format('YYYY-MM-DD') : undefined,
      }).then(resp => {
        this.setFilter({ offset: 0 });
        !this.isCancelled && this.setState(state => ({
          ...state,
          isLoading: false,
          results: mapPayloadToView(resp.data.data, showGroups)
        }))
      }).then(() => {
        this.sortResults(this.state.sortField, this.state.sortOrder);
      })
    }
  };

  exportReport = () => {
    if (!this.state.isDownloading) {
      const { entities, providers, topics, sentiments, startDate, endDate, showGroups } = this.state.filters;
      this.setState(state => ({
        ...state,
        isDownloading: true
      }))

      exportStoreComparisonReport({ entities, providers, topics, sentiments, startDate, endDate, showGroups: !!showGroups }).then(() => {
        this.setState(state => ({
          ...state,
          isDownloading: false
        }))
      })
    }
  };

  onClickSort = (_sortField, mappedOrder) => {
    const { sortField, sortOrder } = this.state;
    if (mappedOrder === sortOrder) {
      /* If radio button or another control with mapped state, then don't fire if already on that sort order */
      return;
    }
    let order = 'DESC';
    if (sortField === _sortField) {
      order = sortOrder === 'DESC' ? 'ASC' : 'DESC'
    }
    this.sortResults(_sortField, order)
    this.setFilter({ offset: 0 });
  }

  sortResults = (sortField, order) => {
    !this.isCancelled && this.setState(state => {
      const applySort = (arr) => {
        const withChildrenSorted = arr.map(item => item.children ? ({ ...item, children: applySort(item.children) }) : item);
        return sortField ? orderBy(withChildrenSorted, r => (
          (sortField === 'locations') ? r.displayName :
            (sortField === 'topPositiveTopic') ? (!!r.topPositiveTopic ? r.topPositiveTopic.occurrences : '') :
              (sortField === 'topNegativeTopic') ? (!!r.topNegativeTopic ? r.topNegativeTopic.occurrences : '') :
                (r.values[sortField].current ? r.values[sortField].current : '')
        ), order.toLowerCase()) : arr
      }

      const sorted = applySort(this.state.results);

      return { results: sorted, filters: { ...state.filters }, sortField, sortOrder: order }
    });
  }

  hideZeroResults = arr => arr
    .map(x => x.children ? { ...x, children: this.hideZeroResults(x.children) } : x)
    .filter(result => {
      return !!Object.keys(result.values).find(val => result.values[val].current !== undefined && result.values[val].current > 0)
    });


  getSortOrder = (sortField) => {
    return this.state.sortField === sortField && this.state.sortOrder
  }

  getLayoutFromTitle = (column) => {
    const title = column.title;
    if (typeof title !== "string") {
      return title;
    }
    const splitTitle = title.split(" ");

    if (!column.classification && splitTitle.length < 2) {
      return title;
    }

    if (column.title.classification) {
      return <React.Fragment>&nbsp;{splitTitle[0]}<br />{splitTitle[1]}</React.Fragment>;
    } else if (column.id === 'topPositiveTopic' || column.id === 'topNegativeTopic') {
      return <React.Fragment>{splitTitle[0]} {splitTitle[1]}<br />{splitTitle[2]}</React.Fragment>;
    } else {
      return <React.Fragment>{splitTitle[0]}</React.Fragment>;
    }
  }

  componentDidMount() {
    accessStore.onceLoaded().then(() => this.setState({ accessStore }))
    this.mapStoreComparisonFiltersToState(this.props.storeComparisonFilters)
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.storeComparisonFilters, this.props.storeComparisonFilters)) {
      this.mapStoreComparisonFiltersToState(this.props.storeComparisonFilters);
    }
  }

  getAppropriateEntityId = () => {
    /* API expects normal entityIds, except in the the case of top-level 'All', when we return account entityId */
    const { selectedEntity } = SelectedLocationsStore;
    return selectedEntity && selectedEntity.entityIdString;
  }

  mapStoreComparisonFiltersToState(storeComparisonFilters) {
    this.setState(state => ({
      filters: {
        ...state.filters, ...storeComparisonFilters,
        ...(
          SelectedLocationsStore.selectedLocations
            ? {entities:  SelectedLocationsStore.selectedEntity.entityIdString }
            : undefined
        ),
        startDate: storeComparisonFilters.startDate,
        endDate: storeComparisonFilters.endDate
      }
    }), debounce(this.search, 500))
  }

  handleNoMentionFilterChange = (event) => {
    const value = event.target.checked;

    this.setState({
      hideResultsWithNoMentions: value,
    }, () => {
      /* 
      * If on last page and 'Hide locations with no mentions' is checked, 
      * we need to update offset key in state.filters as filtered data most 
      * likely has less pages than all results.
      */
      const { filters, results } = this.state;
      if (value) {
        const { offset, limit } = filters;
        const data = this.hideZeroResults(results);
        if (offset > data.length) {
          const newOffset = data.length - limit;
          this.setFilter({ offset: newOffset });
        }
      }
    });
  }

  render() {
    const { isLoading, accessStore, hideResultsWithNoMentions, results, filters } = this.state;
    const { showGroups, offset, limit } = filters;

    const skeletonRows = limit;

    const data = hideResultsWithNoMentions ? this.hideZeroResults(results) : results;
    const pagedResults = this.currentPageResults();

    /* Pagination */
    const currentPage = Math.ceil(offset / limit) + 1;
    const totalPages = Math.ceil(data.length / 15);

    return (
      <div className="card table-container">
        <div className="card-header d-flex flex-row align-items-start align-items-sm-center justify-content-between mb-0">
          <ul className="nav nav-pills">
              <li className="nav-item">
                <a
                  className={`nav-link ${showGroups ? "" : "active"}`}
                  id="showLocations"
                  onClick={e => this.props.onShowGroupsChanged(false)}
                >
                  By Location
                </a>
              </li>
              <li className="nav-item">
                <a
                  className={`nav-link ${showGroups ? "active" : ""}`}
                  id="showGroups"
                  onClick={e => this.props.onShowGroupsChanged(true)}
                >
                  By Group
                </a>
              </li>
            </ul>
            <span className="d-flex flex-column flex-sm-row align-items-end align-items-sm-center">
              <label className="mb-sm-0 mr-sm-3 table-control">
                <input
                  className="mr-2"
                  name="hideResultsWithNoMentions"
                  type="checkbox"
                  checked={this.state.hideResultsWithNoMentions}
                  onChange={this.handleNoMentionFilterChange} />
                Hide locations with no mentions
              </label>
              <button className="btn btn-outline-secondary" onClick={() => this.exportReport()}>
                <i className="fas fa-cloud-download control-spacer-right" />Download
              </button>
            </span>
          </div>
        <div className={`card-body px-0 pb-0 table-scroll-container ${showGroups ? "card-footerless" : ""}`}>
          {/* TODO: Break out into DataTableHeader component */}
          <table className="table table-striped table-borderless table-hover full-width mb-0">
            {/* TODO: Break out into DataTableHeader component */}
            <thead>
              <tr className="sort-headers-row text-center" style={{ height: '49px' }}>
                {columns.map(column =>
                  <th key={'column-heading-cell-' + column.id} className="clickable">
                    {column.sortable
                      ?
                      <SortButton
                        title={<React.Fragment>
                          <SentimentIcon classification={column.classification} className={getClassName(column.title)} />
                          {column.id !== "locations" ? this.getLayoutFromTitle(column) : showGroups ? "Groups" : "Locations"}
                        </React.Fragment>}
                        sortOrder={this.getSortOrder(column.id)}
                        onClick={() => this.onClickSort(column.id)}
                        classNames={`sort-content ${getClassName(column.title)}`}
                      />
                      :
                      <span className="sentiment positive">{column.title}</span>}
                  </th>)}
              </tr>
              {/* TODO: Break out into DataTableSubHeader component */}
              <tr className="sub-header">
                {columns.map(column => <th key={'column-sub-heading-cell-' + column.id} className={`align-middle font-weight-normal ${getClassName(column.id, /(?=[A-Z])/)} sub-head-content`}>{column.subtext}</th>)}
              </tr>
            </thead>
            <tbody>
              {isLoading && [...Array(skeletonRows)].map((e, i) => <SkeletonText key={i} showGroups={showGroups} />)}
              {!isLoading && <React.Fragment>
                {pagedResults.length === 0 &&
                  <tr>
                    <td colSpan={columns.length} style={{ padding: '30px' }}>
                      <div className="text-center">
                        This {SelectedLocationsStore.selectedEntityType} {showGroups ? " does not belong to any groups." : " has no matching data."}
                      </div>
                    </td>
                  </tr>}
                {pagedResults.length > 0 && pagedResults.map((result, index) => {
                  return (
                    <ReportRow
                      key={'report-row-' + index}
                      result={result}
                      entity={accessStore && (result.entityId === "All" ? { name: "All" } : accessStore.byEntityId[result.entityId])}
                      showGroups={showGroups} columns={columns} expandRow={this.expandRow}
                      index={index} levelsDeep={0}
                    />
                  )
                })}
              </React.Fragment>}
            </tbody>
          </table>
        </div>
        {showGroups
          ?
          ""
          :
          data.length > 0
            ?
            <div className="d-flex justify-content-center card-footer">
              <Pagination
                /* Make sure we're not out of range if we've filtered out zero mentions or other data */
                currentPage={currentPage > totalPages ? totalPages : currentPage}
                totalPages={totalPages}
                onChange={(page) => this.setFilter({ offset: (page - 1) * limit })}
              />
            </div>
            :
            <div className="d-flex justify-content-center card-footer">
              <Pagination currentPage={0} totalPages={0} />
            </div>
        }
      </div>
    )
  }
}

const MostTopicHeaders = () => (<span className="d-flex flex-column justify-content-center">
  <span className="d-flex justify-content-center">
    <span className="pr-1 text-right text-capitalize topic-word">
      Topic
      </span>
    <span className="text-nowrap topic-count text-left">
      (Mentions)
      </span>
  </span>
  <span className="d-flex justify-content-center">
    <span className="pr-1 text-right text-capitalize topic-word">
      Attribute
      </span>
    <span className="text-nowrap topic-count text-left">
      (Mentions)
      </span>
  </span>
</span>);

const SkeletonText = (props) => (<tr>
  <td className="p-2 pl-3">
    {props.showGroups ? <div className="skeleton-text-large mb-0"></div> : <React.Fragment>
      <div className="skeleton-text-large"></div>
      <div className="skeleton-text-medium"></div>
      <div className="skeleton-text-large mb-0"></div>
    </React.Fragment>}
  </td>
  <td className="p-2">
    <div className="d-flex flex-column justify-content-center">
      <div className="skeleton-text-small align-self-end mb-0"></div>
    </div>
  </td>
  <td className="p-2">
    <div className="d-flex flex-column justify-content-center">
      <div className="skeleton-text-small align-self-end mb-0"></div>
    </div>
  </td>
  <td className="p-2">
    <div className="d-flex flex-column justify-content-center">
      <div className="skeleton-text-small align-self-end mb-0"></div>
    </div>
  </td >
  <td className="p-2">
    <div className="d-flex flex-column justify-content-center">
      <div className="skeleton-text-small align-self-end mb-0"></div>
    </div>
  </td >
  <td className="p-2">
    <div className="d-flex flex-column justify-content-center">
      <div className="skeleton-text-medium align-self-end"></div>
      <div className="skeleton-text-medium align-self-end mb-0"></div>
    </div>
  </td>
  <td className="p-4">
    <div className="d-flex flex-column justify-content-center">
      <div className="skeleton-text-medium align-self-end"></div>
      <div className="skeleton-text-medium align-self-end mb-0"></div>
    </div>
  </td>
</tr >);